<template>
  <div class="reports-container">
    <div class="reports-hub-view">
      <!-- Search Bar -->
      <div class="search-bar-container mb-2 d-flex align-items-center">
        <feather-icon
          icon="SearchIcon"
          size="16"
          class="search-icon text-muted flex-shrink-0"
        />
        <label
          for="search-bar"
          class="sr-only"
        >
          Search reports
        </label>
        <input
          id="search-bar"
          v-model="searchQuery"
          type="text"
          placeholder="Search reports..."
          class="search-input w-100"
        >
      </div>

      <!-- Report Category Cards -->
      <b-row>
        <b-col
          v-for="category in filteredCategories"
          :id="category.id"
          :key="category.id"
          cols="12"
        >
          <b-card
            class="category-card shadow-sm border-0"
            :class="{ 'highlighted-category-card': category.id === highlightedCategoryId }"
          >
            <!-- Category Title -->
            <h5 class="font-weight-bolder category-title mb-1">
              {{ category.title }}
            </h5>

            <!-- Reports 2-Column Grid -->
            <b-row>
              <b-col
                v-for="item in category.items"
                :key="item.key"
                cols="12"
                md="6"
                class="py-25"
              >
                <div
                  class="report-item-row d-flex align-items-center justify-content-between px-1 py-75 rounded cursor-pointer"
                  @click="selectReport(item)"
                >
                  <!-- Icon & Title -->
                  <div class="d-flex align-items-center min-w-0 mr-1">
                    <feather-icon
                      :icon="item.icon"
                      size="18"
                      class="report-icon mr-1 flex-shrink-0"
                    />
                    <span class="report-title text-truncate">
                      {{ item.title }}
                    </span>
                  </div>

                  <!-- Right Chevron -->
                  <feather-icon
                    icon="ChevronRightIcon"
                    size="16"
                    class="chevron-icon flex-shrink-0"
                  />
                </div>
              </b-col>
            </b-row>
          </b-card>
        </b-col>
      </b-row>

      <!-- Empty Search State -->
      <b-card
        v-if="filteredCategories.length === 0"
        class="no-results-card text-center border-0 shadow-sm py-2"
      >
        <h5 class="no-results-title mb-50">
          No reports found
        </h5>
        <p class="no-results-subtitle mb-0">
          Nothing matches “{{ searchQuery }}”. Try a different keyword.
        </p>
      </b-card>
    </div>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol,
} from 'bootstrap-vue'
import { mapGetters } from 'vuex'
import store from '@/store'

export default {
  components: {
    BCard,
    BRow,
    BCol,
  },
  data() {
    return {
      store,
      searchQuery: '',
      categories: [
        {
          id: 'operations-warehouse',
          title: 'Operations & Warehouse',
          items: [
            {
              key: 'operations-report',
              title: 'Operations Report',
              icon: 'ActivityIcon',
              routeName: 'operations-reports',
              permissions: ['operations-reschedule-report'],
            },
            {
              key: 'delivery-progress-report',
              title: 'Delivery Progress Report',
              icon: 'TruckIcon',
              routeName: 'branch-delivery-progress',
              permissions: ['branch-delivery-progress-report'],
            },
            {
              key: 'assigned-orders-report',
              title: 'Assigned Orders Report',
              icon: 'FileTextIcon',
              routeName: 'assigned-orders-reports',
              permissions: ['assigned-orders-to-delivery-rider-report', 'assigned-orders-to-pickup-rider-report', 'returned-orders-to-merchant-report', 'returned-orders-to-origin-warehouse-report', 'assigned-orders-to-warehouse-report', 'returned-orders-to-origin-warehouse-invalid-destination-report', 'received-orders-to-origin-warehouse-report'],
            },
            {
              key: 'assigned-order-summary-report',
              title: 'Assigned Order Summary Report',
              icon: 'SlidersIcon',
              routeName: 'assign-order-summery-report',
              permissions: ['assign-order-summery-report'],
            },
            {
              key: 'warehouse-general-report',
              title: 'Warehouse General Report',
              icon: 'ArchiveIcon',
              routeName: 'warehouse-reports',
              permissions: ['temporary-warehouse-report'],
            },
          ],
        },
        {
          id: 'merchants-sales',
          title: 'Merchants & Sales',
          items: [
            {
              key: 'sales-report',
              title: 'Sales Report',
              icon: 'BarChart2Icon',
              routeName: 'monthly-sales',
              permissions: ['sales-report'],
            },
            {
              key: 'merchant-status-count-report',
              title: 'Merchant Status Count Report',
              icon: 'UsersIcon',
              routeName: 'merchant-status-count',
              permissions: ['merchant-status-count-report'],
            },
            {
              key: 'merchant-aging-report',
              title: 'Merchant Aging Report',
              icon: 'ClockIcon',
              routeName: 'aging-report',
              permissions: ['view-report'],
            },
            {
              key: 'warehouse-status-count-report',
              title: 'Warehouse Status Count Report',
              icon: 'BoxIcon',
              routeName: 'warehouse-status-count',
              permissions: ['warehouse-status-count-report'],
            },
            {
              key: 'customer-feedback-report',
              title: 'Customer Feedback Report',
              icon: 'MessageSquareIcon',
              routeName: 'all-feedbacks',
              permissions: ['end-customer-feedback-report'],
            },
          ],
        },
        {
          id: 'finance-billing',
          title: 'Finance & Billing',
          items: [
            {
              key: 'branch-expenses-report',
              title: 'Branch Expenses Report',
              icon: 'FileMinusIcon',
              routeName: 'branch-expenses-report',
              permissions: ['branch-expenses-report'],
            },
            {
              key: 'to-be-invoiced-report',
              title: 'To Be Invoiced Report',
              icon: 'FileTextIcon',
              routeName: 'to-be-invoiced-report',
              permissions: ['to-be-invoiced-report'],
            },
            {
              key: 'branch-progress-report',
              title: 'Branch Progress Report',
              icon: 'TrendingUpIcon',
              routeName: 'branch-progress-report',
              permissions: ['branch-progress-report'],
            },
            {
              key: 'return-free-report',
              title: 'Return Free Report',
              icon: 'RotateCcwIcon',
              routeName: 'return-free-merchants-report',
              permissions: ['return-free-client-report'],
            },
          ],
        },
        {
          id: 'downloads',
          title: 'Downloads',
          items: [
            {
              key: 'download-all-orders',
              title: 'All Orders',
              icon: 'DownloadIcon',
              routeName: 'all-order-downloads',
              params: { key: 'all-orders' },
            },
            {
              key: 'download-merchant-invoices',
              title: 'Merchant Invoices',
              icon: 'FilePlusIcon',
              routeName: 'merchant-invoice-downloads',
              params: { key: 'merchant-invoice' },
            },
            {
              key: 'download-assigned-orders',
              title: 'Assigned Orders',
              icon: 'FileTextIcon',
              routeName: 'assign-order-downloads',
            },
          ],
        },
        {
          id: 'staff-productivity',
          title: 'Staff Productivity',
          items: [
            {
              key: 'rider-performance-report',
              title: 'Rider Performance Report',
              icon: 'UserIcon',
              routeName: 'rider-performance-report',
              permissions: ['rider-performance-report'],
            },
            {
              key: 'branch-performance-report',
              title: 'Branch Performance Report',
              icon: 'TrelloIcon',
              routeName: 'branch-performance-report',
              permissions: ['branch-performance-report'],
            },
            {
              key: 'branch-dispatched-report',
              title: 'Branch Wise Dispatched Report',
              icon: 'HomeIcon',
              routeName: 'branch-dispatched-report',
              permissions: ['branch-dispatched-report'],
            },
            {
              key: 'merchant-dispatched-report',
              title: 'Merchant Wise Dispatched Report',
              icon: 'UsersIcon',
              routeName: 'merchant-dispatched-report',
              permissions: ['merchant-dispatched-report'],
            },
            {
              key: 'merchant-inactivation-report',
              title: 'Merchant Inactivation Report',
              icon: 'UserXIcon',
              routeName: 'merchant-inactivation-report',
              permissions: ['merchant-inactivation-report'],
            },
          ],
        },
        {
          id: 'crm',
          title: 'CRM',
          items: [
            {
              key: 'merchant-wise-ticket-report',
              title: 'Merchant Wise Ticket Report',
              icon: 'UserIcon',
              routeName: 'merchant-wise-ticket-report',
              permissions: ['merchant-wise-ticket-report'],
            },
            {
              key: 'warehouse-wise-ticket-report',
              title: 'Warehouse Wise Ticket Report',
              icon: 'HomeIcon',
              routeName: 'warehouse-wise-ticket-report',
              permissions: ['warehouse-wise-ticket-report'],
            },
            {
              key: 'status-wise-ticket-report',
              title: 'Status Wise Ticket Report',
              icon: 'OctagonIcon',
              routeName: 'status-wise-ticket-report',
              permissions: ['status-wise-ticket-report'],
            },
            {
              key: 'role-wise-ticket-report',
              title: 'Role Wise Ticket Report',
              icon: 'UsersIcon',
              routeName: 'role-wise-ticket-report',
              permissions: ['role-wise-ticket-report'],
            },
          ],
        },
      ],
    }
  },
  computed: {
    ...mapGetters(['currentUser']),

    highlightedCategoryId() {
      return this.$route.query.category || null
    },

    filteredCategories() {
      const query = this.searchQuery.toLowerCase().trim()

      return this.categories
        .map(category => {
          const visibleItems = category.items.filter(item => {
            const hasPerm = this.checkPermission(item.permissions)
            if (!hasPerm) return false

            if (query && !item.title.toLowerCase().includes(query)) return false

            return true
          })

          return {
            ...category,
            items: visibleItems,
          }
        })
        .filter(category => category.items.length > 0)
    },
  },
  mounted() {
    this.scrollToHighlightedCategory()
  },
  methods: {
    checkPermission(permissions) {
      if (!permissions || permissions.length === 0) return true
      const userPermissions = this.store.getters.permissions || []
      return permissions.some(p => userPermissions.includes(p))
    },

    scrollToHighlightedCategory() {
      const categoryId = this.$route.query.category
      if (categoryId) {
        this.$nextTick(() => {
          const el = document.getElementById(categoryId)
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }
        })
      }
    },

    selectReport(item) {
      if (item && item.routeName) {
        this.$router.push({ name: item.routeName, params: item.params || {} })
      }
    },
  },
}
</script>

<style scoped>
/* Clean search container */
.search-bar-container {
  background-color: #ffffff;
  border: 1px solid #e4e6eb;
  border-radius: 6px;
  box-shadow: 0 0 8px rgba(16, 24, 40, 0.08);
  padding: 12px 18px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.search-bar-container:focus-within {
  border-color: var(--primary);
  box-shadow: 0 0 8px var(--primary);
}

.search-icon {
  color: #98a2b3 !important;
  margin-right: 12px;
}

.search-input {
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
  background: transparent;
  font-size: 0.92rem;
  color: #344054;
  padding: 0;
  line-height: 1.5;
}

.search-input::placeholder {
  color: #98a2b3;
  font-size: 0.92rem;
}

.category-card {
  border-radius: 8px !important;
  background-color: #ffffff;
  transition: box-shadow 0.3s ease, border-color 0.3s ease;
  border: 1px solid #ebe9f1 !important;
}

.highlighted-category-card {
  box-shadow: 0 0 0 3px rgba(44, 107, 239, 0.25) !important;
  border: 1.5px solid #26cbef !important;
}

.category-title {
  color: #1e1e1e;
  font-size: 1.05rem;
  font-weight: 700;
}

.min-w-0 {
  min-width: 0;
}

.report-item-row {
  transition: all 0.15s ease-in-out;
  color: #4b465c;
  background-color: transparent;
}

.report-item-row:hover {
  background-color: #f8f9fa;
}

.report-icon {
  color: #6e6b7b;
}

.chevron-icon {
  color: #b9b9c3;
  transition: transform 0.15s ease-in-out;
}

.report-item-row:hover .chevron-icon {
  transform: translateX(2px);
  color: #5e5873;
}

.report-title {
  font-size: 0.92rem;
  font-weight: 500;
  color: #333333;
}

.no-results-card {
  border-radius: 8px !important;
  background-color: #ffffff;
  border: 1px solid #ebe9f1 !important;
}

.no-results-title {
  font-size: 1rem;
  font-weight: 700;
  color: #1e1e1e;
}

.no-results-subtitle {
  font-size: 0.9rem;
  color: #6e6b7b;
}
</style>
